import { Dispatch, SetStateAction, useMemo, useState } from "react";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import { Box, Stack, TableCell, TableRow, Typography } from "@mui/material";
import PaginationTable from "../table/PaginationTable";
import { HeadColType } from "src/types/paginationTypes";
import SuccessStatusChip from "../common/SuccessStatusChip";
import { ProgressStatusEnum } from "src/enums/allBidsPageEnums";
import PopupButton from "../common/PopupButton";
import { useNavigate } from "react-router-dom";
import useMessage from "src/hooks/useMessage";
import { OrderResponse } from "./BackorderModel";
import EmptyTable from "../table/EmptyTable";
import { responseTableRow } from "./modelStyles";

type OrderResponseModel = {
    isOpen: boolean;
    orderResponse: OrderResponse[];
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    setReloadData: Dispatch<SetStateAction<number>>;
};

const headRow: HeadColType[] = [
    {
        label: "Domain",
        align: "center",
    },
    {
        label: "Status",
        align: "center",
    },
    {
        label: "Message",
        align: "center",
    },
];

const AllBidsResponseModel = (props: OrderResponseModel) => {
    const { isOpen, orderResponse, setIsOpen, setReloadData } = props;

    const navigate = useNavigate();
    const { showSuccess } = useMessage();

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage] = useState<number>(4);

    const { successCount, failureCount } = useMemo(() => {
        return orderResponse.reduce(
            (counts, response) => {
                response.success
                    ? counts.successCount++
                    : counts.failureCount++;
                return counts;
            },
            { successCount: 0, failureCount: 0 },
        );
    }, [orderResponse]);

    const visibleResponse = useMemo(() => {
        return orderResponse.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        );
    }, [orderResponse, page, rowsPerPage]);

    const handleClose = () => {
        setIsOpen(false);
        if (successCount > 0) {
            if (window.location.pathname === "/dashboard/preBids") {
                setReloadData(Date.now() / 1000);
            } else {
                navigate("/dashboard/preBids");
            }
            showSuccess("Domains Added Successfully");
        }
    };

    return (
        <PopupModelWrapper
            open={isOpen}
            handleClose={handleClose}
            width={{ md: 650, sm: 350, xs: 280 }}
            title="Response Status"
        >
            <Stack sx={{ my: 3 }}>
                <Stack direction="row" sx={{ width: "auto" }} spacing={2}>
                    <CountChip
                        type="success"
                        value={`${successCount}/${successCount + failureCount}`}
                        name="Success"
                    />
                    <CountChip
                        type="danger"
                        value={`${failureCount}/${successCount + failureCount}`}
                        name="Failed"
                    />
                </Stack>
                <Box sx={{ mt: 3 }}>
                    <PaginationTable
                        rowsCount={orderResponse.length}
                        rowsPerPage={rowsPerPage}
                        headRow={headRow}
                        minWidth={500}
                        page={page + 1}
                        setPage={setPage}
                    >
                        {visibleResponse.length > 0 ? (
                            visibleResponse.map((row: OrderResponse) => (
                                <TableRow
                                    key={row.domain}
                                    sx={responseTableRow}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        {row.domain}
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <SuccessStatusChip
                                            type={
                                                row.success
                                                    ? ProgressStatusEnum.SUCCESS
                                                    : ProgressStatusEnum.FAILURE
                                            }
                                        />
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        sx={{
                                            color: row.success
                                                ? "#52C41A"
                                                : "#FF4D4F",
                                        }}
                                    >
                                        {row.reason}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <EmptyTable colSpan={4} msg="No Data" />
                        )}
                    </PaginationTable>
                </Box>
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
                <PopupButton triggerFunction={handleClose} type="proceed">
                    Done
                </PopupButton>
            </Stack>
        </PopupModelWrapper>
    );
};

// Success Failed chips in top of the popup
const CountChip = ({
    name,
    value,
    type,
}: {
    name: string;
    value: string;
    type: "success" | "danger";
}) => {
    const colorStyles = {
        bgcolor: type === "success" ? "#F6FFED" : "#FFF1F0",
        color: type === "success" ? "#52C41A" : "#FF4D4F",
    };
    return (
        <Box
            sx={{
                ...colorStyles,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: 1,
            }}
        >
            <Typography sx={{ fontSize: "13px", mr: 2 }}>{name}</Typography>
            <Typography sx={{ fontSize: "13px", ml: 2 }}>{value}</Typography>
        </Box>
    );
};

export default AllBidsResponseModel;
